import type {
  IFillStyle,
  IKuikaStyle,
  IProject,
  IStylingBorder,
  IStylingEffects,
  IStylingPosition,
  IStylingShadow,
  IStylingTypography
} from "@kuika/kuika-cl-model";
import _ from "lodash";

/**
 * Pure style-tag ("unified style") logic shared by the styling panel's tag selector and the canvas
 * selection bar's style-tag chip. A tag is a group of up to six project styling entries (position, text,
 * fill, border, shadow, effects) sharing a `groupId`; a legacy tag is one entry without a group. Tags
 * STACK on a component: applying one adds its entry ids to the section `*Ids` arrays of the (form-shaped)
 * styleObj, removing one filters them out. Nothing here touches `styleGroupId` / `styleGroupName`.
 */

export type PanelType = "Layout" | "Text" | "Fill" | "Border" | "Shadow" | "Effects";

export interface IUnifiedStyleItem {
  id: string; // groupId for unified styles, or individual style id for legacy
  groupId: string | null;
  displayName: string; // Either the group name or "styleName_PanelName"
  panelType?: PanelType; // For legacy styles without groupId
  isLegacy: boolean; // True if no groupId (existing style before this feature)
  styles: IUnifiedStyleParts;
}

export interface IUnifiedStyleParts {
  position?: IStylingPosition;
  text?: IStylingTypography;
  fill?: IFillStyle;
  border?: IStylingBorder;
  shadow?: IStylingShadow;
  effects?: IStylingEffects;
}

/**
 * A `*Ids` chain (`positionIds`, `fillIds`, …) lists the style tags a component wears. It is
 * derived from the `|`-joined id the DI persists, and while the canonical converters in
 * StyleHelper drop empty segments on the way through, not every writer does — so saved screens
 * carry entries the `string[]` in the models does not admit, `fillIds: ["", null]` among them.
 * Lowercasing such an entry throws `Cannot read properties of null (reading 'toLowerCase')`, which
 * took down the styling panel and the canvas selection bar the moment such a component was
 * selected. These two helpers are the only places that have to know it; go through them rather
 * than lowercasing a chain id inline.
 */
export const isUsableStyleId = (id: string | null | undefined): id is string => typeof id === "string" && id.length > 0;

export const styleIdEquals = (a: string | null | undefined, b: string | null | undefined): boolean =>
  isUsableStyleId(a) && isUsableStyleId(b) && a.toLowerCase() === b.toLowerCase();

type StyleSectionKey = keyof IUnifiedStyleParts;

/** The project's six styling collections — all the tag list reads. */
export type IStyleTagProject = Pick<
  IProject,
  "stylingPosition" | "stylingTypography" | "stylingFill" | "stylingBorder" | "stylingShadow" | "stylingEffects"
>;

type StyleSectionEntry =
  | IStylingPosition
  | IStylingTypography
  | IFillStyle
  | IStylingBorder
  | IStylingShadow
  | IStylingEffects;

interface ISectionSpec {
  section: StyleSectionKey;
  panelType: PanelType;
  /** The platform default entries of this section are never offered as tags. */
  defaultName: RegExp;
  entryId: (entry: StyleSectionEntry) => string | null | undefined;
}

// Order matters: it is the order the panel has always listed legacy entries in.
const SECTION_SPECS: readonly ISectionSpec[] = [
  {
    section: "position",
    panelType: "Layout",
    defaultName: /KUIKA-[a-zA-Z]+-DEFAULT-POSITION(#[a-zA-Z]+#)*$/m,
    entryId: (e: StyleSectionEntry): string | null | undefined => (e as IStylingPosition).positionId
  },
  {
    section: "text",
    panelType: "Text",
    defaultName: /KUIKA-[a-zA-Z]+-DEFAULT-TYPO(#[a-zA-Z]+#)*$/m,
    entryId: (e: StyleSectionEntry): string | null | undefined => (e as IStylingTypography).typoId
  },
  {
    section: "fill",
    panelType: "Fill",
    defaultName: /KUIKA-[a-zA-Z]+-DEFAULT-FILL(#[a-zA-Z]+#)*$/m,
    entryId: (e: StyleSectionEntry): string | null | undefined => (e as IFillStyle).fillId
  },
  {
    section: "border",
    panelType: "Border",
    defaultName: /KUIKA-[a-zA-Z]+-DEFAULT-BORDER(#[a-zA-Z]+#)*$/m,
    entryId: (e: StyleSectionEntry): string | null | undefined => (e as IStylingBorder).borderId
  },
  {
    section: "shadow",
    panelType: "Shadow",
    defaultName: /KUIKA-[a-zA-Z]+-DEFAULT-SHADOW(#[a-zA-Z]+#)*$/m,
    entryId: (e: StyleSectionEntry): string | null | undefined => (e as IStylingShadow).shadowId
  },
  {
    section: "effects",
    panelType: "Effects",
    defaultName: /KUIKA-[a-zA-Z]+-DEFAULT-EFFECTS(#[a-zA-Z]+#)*$/m,
    entryId: (e: StyleSectionEntry): string | null | undefined => (e as IStylingEffects).effectsId
  }
];

const collectionOf = (project: IStyleTagProject, section: StyleSectionKey): StyleSectionEntry[] | undefined => {
  switch (section) {
    case "position":
      return project.stylingPosition;
    case "text":
      return project.stylingTypography;
    case "fill":
      return project.stylingFill;
    case "border":
      return project.stylingBorder;
    case "shadow":
      return project.stylingShadow;
    case "effects":
      return project.stylingEffects;
    default: {
      const unhandled: never = section;
      throw new Error(`Unknown style section: ${String(unhandled)}`);
    }
  }
};

const nameOf = (entry: StyleSectionEntry): string | undefined => (entry as IStylingTypography).name;
const groupIdOf = (entry: StyleSectionEntry): string | null | undefined => (entry as IStylingTypography).groupId;

/** Every tag the project offers: legacy entries first (in section order), then the grouped tags. */
export function buildUnifiedStyleList(project: IStyleTagProject): IUnifiedStyleItem[] {
  const unifiedStyles: IUnifiedStyleItem[] = [];
  const groupedStyles: Map<string, IUnifiedStyleItem> = new Map<string, IUnifiedStyleItem>();

  SECTION_SPECS.forEach((spec: ISectionSpec): void => {
    collectionOf(project, spec.section)?.forEach((entry: StyleSectionEntry): void => {
      const groupId: string | null | undefined = groupIdOf(entry);
      if (groupId) {
        let item: IUnifiedStyleItem | undefined = groupedStyles.get(groupId);
        if (!item) {
          item = { id: groupId, groupId, displayName: nameOf(entry) || "", isLegacy: false, styles: {} };
          groupedStyles.set(groupId, item);
        }
        (item.styles as Record<StyleSectionKey, StyleSectionEntry>)[spec.section] = entry;
        return;
      }
      const id: string | null | undefined = spec.entryId(entry);
      const name: string | undefined = nameOf(entry);
      if (id && name && !spec.defaultName.test(name)) {
        unifiedStyles.push({
          id,
          groupId: null,
          displayName: `${name}_${spec.panelType}`,
          panelType: spec.panelType,
          isLegacy: true,
          styles: { [spec.section]: entry }
        });
      }
    });
  });

  groupedStyles.forEach((item: IUnifiedStyleItem): void => {
    unifiedStyles.push(item);
  });
  return unifiedStyles;
}

// ── The ids a (form-shaped) styleObj carries per section ───────────────────────────────────────

interface ISectionIds {
  section: StyleSectionKey;
  get: (style: IKuikaStyle) => string[] | null | undefined;
  /** Creates the section when missing, then sets its ids array. */
  set: (style: IKuikaStyle, ids: string[]) => void;
  /** The pipe-joined DI form of the same ids. */
  getJoined: (style: IKuikaStyle) => string | null | undefined;
}

const SECTION_IDS: readonly ISectionIds[] = [
  {
    section: "position",
    get: (s: IKuikaStyle): string[] | null | undefined => s.position?.positionIds,
    set: (s: IKuikaStyle, ids: string[]): void => {
      s.position = { ...(s.position ?? ({} as IStylingPosition)), positionIds: ids };
    },
    getJoined: (s: IKuikaStyle): string | null | undefined => s.position?.positionId
  },
  {
    section: "text",
    get: (s: IKuikaStyle): string[] | null | undefined => s.text?.typoIds,
    set: (s: IKuikaStyle, ids: string[]): void => {
      s.text = { ...(s.text ?? ({} as IStylingTypography)), typoIds: ids };
    },
    getJoined: (s: IKuikaStyle): string | null | undefined => s.text?.typoId
  },
  {
    section: "fill",
    get: (s: IKuikaStyle): string[] | null | undefined => s.fill?.fillIds,
    set: (s: IKuikaStyle, ids: string[]): void => {
      s.fill = { ...(s.fill ?? ({} as IFillStyle)), fillIds: ids };
    },
    getJoined: (s: IKuikaStyle): string | null | undefined => s.fill?.fillId
  },
  {
    section: "border",
    get: (s: IKuikaStyle): string[] | null | undefined => s.border?.borderIds,
    set: (s: IKuikaStyle, ids: string[]): void => {
      s.border = { ...(s.border ?? ({} as IStylingBorder)), borderIds: ids };
    },
    getJoined: (s: IKuikaStyle): string | null | undefined => s.border?.borderId
  },
  {
    section: "shadow",
    get: (s: IKuikaStyle): string[] | null | undefined => s.shadow?.shadowIds,
    set: (s: IKuikaStyle, ids: string[]): void => {
      s.shadow = { ...(s.shadow ?? ({} as IStylingShadow)), shadowIds: ids };
    },
    getJoined: (s: IKuikaStyle): string | null | undefined => s.shadow?.shadowId
  },
  {
    section: "effects",
    get: (s: IKuikaStyle): string[] | null | undefined => s.effects?.effectsIds,
    set: (s: IKuikaStyle, ids: string[]): void => {
      s.effects = { ...(s.effects ?? ({} as IStylingEffects)), effectsIds: ids };
    },
    getJoined: (s: IKuikaStyle): string | null | undefined => s.effects?.effectsId
  }
];

const PANEL_SECTION: Record<PanelType, StyleSectionKey> = {
  Layout: "position",
  Text: "text",
  Fill: "fill",
  Border: "border",
  Shadow: "shadow",
  Effects: "effects"
};

const specFor = (section: StyleSectionKey): ISectionIds =>
  SECTION_IDS.find((s: ISectionIds): boolean => s.section === section) as ISectionIds;

const partId = (item: IUnifiedStyleItem, section: StyleSectionKey): string | null | undefined => {
  const entry: StyleSectionEntry | undefined = item.styles[section];
  return entry ? SECTION_SPECS.find((s: ISectionSpec): boolean => s.section === section)?.entryId(entry) : undefined;
};

/** The ids a tag contributes, per section: one for a legacy tag, one per defined part for a group. */
function tagIdsBySection(item: IUnifiedStyleItem): Map<StyleSectionKey, string> {
  const out: Map<StyleSectionKey, string> = new Map<StyleSectionKey, string>();
  if (item.isLegacy) {
    const section: StyleSectionKey | undefined = item.panelType ? PANEL_SECTION[item.panelType] : undefined;
    if (section && item.styles[section]) {
      out.set(section, item.id);
    }
    return out;
  }
  SECTION_IDS.forEach(({ section }: ISectionIds): void => {
    const id: string | null | undefined = partId(item, section);
    if (id) {
      out.set(section, id);
    }
  });
  return out;
}

/** Adds a tag to a FORM-shaped styleObj (mutates `style`, like the panel's form values). */
export function applyStyleTagIds(style: IKuikaStyle, item: IUnifiedStyleItem): void {
  tagIdsBySection(item).forEach((id: string, section: StyleSectionKey): void => {
    const spec: ISectionIds = specFor(section);
    const ids: string[] = [...(spec.get(style) ?? [])];
    if (!ids.includes(id)) {
      ids.push(id);
    }
    spec.set(style, ids);
  });
}

/** Removes a tag from a FORM-shaped styleObj (mutates `style`). Ids compare case-insensitively. */
export function removeStyleTagIds(style: IKuikaStyle, item: IUnifiedStyleItem): void {
  tagIdsBySection(item).forEach((id: string, section: StyleSectionKey): void => {
    const spec: ISectionIds = specFor(section);
    const current: string[] | null | undefined = spec.get(style);
    if (current) {
      spec.set(
        style,
        current.filter((x: string | null): boolean => !styleIdEquals(x, id))
      );
    }
  });
}

/** The tags (item ids, in first-applied order) a FORM-shaped styleObj carries. */
export function getAppliedStyleTagIds(style: IKuikaStyle, list: IUnifiedStyleItem[]): string[] {
  const selectedIds: string[] = [];
  SECTION_IDS.forEach((spec: ISectionIds): void => {
    // Empty and detached slots ("" / null) stay in the stored chain but never name a tag.
    selectedIds.push(...(spec.get(style) ?? []).filter(isUsableStyleId));
  });
  const groupIds: Set<string> = new Set<string>();
  selectedIds.forEach((id: string): void => {
    const item: IUnifiedStyleItem | undefined = list.find((u: IUnifiedStyleItem): boolean => {
      if (u.isLegacy) {
        return u.id === id;
      }
      return SECTION_IDS.some(({ section }: ISectionIds): boolean => styleIdEquals(partId(u, section), id));
    });
    if (item) {
      groupIds.add(item.id);
    }
  });
  return Array.from(groupIds);
}

const splitJoined = (joined: string | null | undefined): string[] =>
  typeof joined === "string" ? joined.split("|").filter((id: string): boolean => id.length > 0) : [];

/**
 * A DI styleObj → a FORM-shaped deep copy the commit path accepts. The commit
 * (`StyleHelper.convertFormStyleToDiStyle`) REBUILDS each section's pipe-joined `*Id` from its `*Ids`
 * array, so a stored styleObj that carries only the joined id would lose its tags on the next write.
 * Every section that has a joined id but no ids array gets the array back.
 */
export function toFormStyleIds(styleObj: IKuikaStyle | undefined): IKuikaStyle {
  const copy: IKuikaStyle = styleObj ? _.cloneDeep(styleObj) : ({} as IKuikaStyle);
  SECTION_IDS.forEach((spec: ISectionIds): void => {
    const ids: string[] | null | undefined = spec.get(copy);
    if (!ids || ids.length === 0) {
      const joined: string[] = splitJoined(spec.getJoined(copy));
      if (joined.length > 0) {
        spec.set(copy, joined);
      }
    }
  });
  return copy;
}
